import { routeParts, type ErrorCode, type Route } from "@nabla/api";
import { loadApi, readSent, type Pick } from "./answers.ts";

export type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export interface Reach {
  readonly fetch: Fetch;
  readonly api: string;
  readonly seen: (info: Seen) => void;
}

export type ClientError =
  | { readonly kind: "api"; readonly status: number; readonly code: ErrorCode; readonly message: string; readonly retryable: boolean; readonly context?: Record<string, string | number>; readonly field?: string; readonly detail?: string; readonly requestId?: string }
  | { readonly kind: "rede" }
  | { readonly kind: "formato"; readonly detail: string };

export interface Held {
  readonly body: object;
  readonly sameApi: boolean;
}

type Answer = { readonly ok: true; readonly held: Held } | Failed;
type Failed = { readonly ok: false; readonly error: ClientError };
const malformed = (detail: string): Failed => ({ ok: false, error: { kind: "formato", detail } });

export interface Seen {
  readonly versionMismatch: boolean;
}

export function signedOutBy(error: ClientError, signOut: () => void): boolean {
  const out = error.kind === "api" && error.code === "nao-autenticado";
  if (out) signOut();
  return out;
}

export function fillPath(path: string, params: Readonly<Record<string, string>>): string {
  return path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`${path} needs the parameter ${name}`);
    return encodeURIComponent(value);
  });
}

export async function getJson(host: Reach, url: string): Promise<Answer> {
  const response = await reach(host.fetch, url, { headers: { Accept: "application/json" }, cache: "no-store" });
  if (!response) return { ok: false, error: { kind: "rede" } };
  const sameApi = sameApiOf(host, response);
  if (!response.ok) return { ok: false, error: await errorOf(response, sameApi) };
  const body = await bodyOf(response);
  if (!body.ok) return malformed(body.detail);
  if (typeof body.value !== "object" || body.value === null) return malformed(`${url} não é um objeto`);
  if (!sameApi) await loadApi();
  return { ok: true, held: { body: body.value, sameApi } };
}

export type Reply<T> = { readonly kind: "feito"; readonly value: T } | { readonly kind: "pendente" } | { readonly kind: "falhou"; readonly error: ClientError };

export function sendJson<T>(host: Reach, route: Route, params: Readonly<Record<string, string>>, body: unknown, answer: Pick<T> | null): Promise<Reply<T | null>> {
  const json = { type: "application/json", body: JSON.stringify(body) };
  return sendTo(host, route, fillPath(routeParts(route).path, params), json, answer);
}

type Strings = Readonly<Record<string, string>>;

type To = { readonly route: Route; readonly params: Strings; readonly query: Strings };

export function sendBytes<T>(host: Reach, to: To, bytes: Blob, answer: Pick<T>) {
  const url = `${fillPath(routeParts(to.route).path, to.params)}?${new URLSearchParams(to.query)}`;
  return sendTo(host, to.route, url, { type: "application/octet-stream", body: bytes }, answer);
}

type Body = { readonly type: string; readonly body: string | Blob };

async function sendTo<T>(host: Reach, route: Route, url: string, sent: Body, answer: Pick<T> | null): Promise<Reply<T | null>> {
  const headers = { "Content-Type": sent.type, "X-Nabla": "1", Accept: "application/json" };
  const keepalive = typeof sent.body === "string";
  const response = await reach(host.fetch, url, { method: routeParts(route).method, headers, body: sent.body, keepalive });
  if (!response) return { kind: "falhou", error: { kind: "rede" } };
  const sameApi = sameApiOf(host, response);
  if (!response.ok) return { kind: "falhou", error: await errorOf(response, sameApi) };
  if (response.status === 204) return { kind: "feito", value: null };
  const read = await bodyOf(response);
  if (response.status === 202 && read.ok && (await pending(read.value, sameApi))) return { kind: "pendente" };
  if (answer === null) return { kind: "feito", value: null };
  if (!read.ok) return { kind: "falhou", error: { kind: "formato", detail: read.detail } };
  const parsed = await readSent(read.value, sameApi, answer);
  return parsed.ok ? { kind: "feito", value: parsed.value } : { kind: "falhou", error: { kind: "formato", detail: parsed.detail } };
}

async function pending(value: unknown, sameApi: boolean): Promise<boolean> {
  if (sameApi) return typeof value === "object" && value !== null && "pendente" in value;
  return (await readSent(value, sameApi, (api) => api.Pending)).ok;
}

async function reach(fetch: Fetch, url: string, init: RequestInit): Promise<Response | undefined> {
  try {
    return await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return undefined;
  }
}

export function sameApiOf(host: Reach, response: Response): boolean {
  const version = response.headers.get("X-Nabla-Api");
  host.seen({ versionMismatch: version !== null && version !== host.api });
  return version === host.api;
}

async function bodyOf(response: Response): Promise<{ readonly ok: true; readonly value: unknown } | { readonly ok: false; readonly detail: string }> {
  try {
    const value: unknown = await response.json();
    return { ok: true, value };
  } catch {
    return { ok: false, detail: `${response.url || "a resposta"} não é JSON` };
  }
}

async function errorOf(response: Response, sameApi: boolean): Promise<ClientError> {
  const read = await bodyOf(response);
  const parsed = read.ok ? await readSent(read.value, sameApi, (api) => api.ApiError) : undefined;
  if (!parsed?.ok) return response.status >= 502 ? { kind: "rede" } : { kind: "formato", detail: `HTTP ${response.status} sem o formato de erro da API` };
  const { code, message, retryable, context, field, detail } = parsed.value.error;
  const requestId = response.headers.get("X-Request-Id");
  return {
    kind: "api",
    status: response.status,
    code,
    message,
    retryable,
    ...(context === undefined ? {} : { context }),
    ...(field === undefined ? {} : { field }),
    ...(detail === undefined ? {} : { detail }),
    ...(requestId === null ? {} : { requestId }),
  };
}
